import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Copy,
  Globe,
  Search,
  ChevronDown,
  Mail,
  Share2,
  Check,
  LayoutGrid,
} from 'lucide-react';
import { clsx } from 'clsx';
import { resolveWebAppOrigin } from '@/auth/password-reset';
import {
  getSharesByOwner,
  createShare,
  updateShare,
  buildPublicShareUrl,
  type Share,
  type ShareMember,
  type ShareRole,
  type ProjectSectionKey,
} from '@/api/shares';
import { notifications } from '@/utils/notifications';
import { useDialogA11y } from '@/hooks/useDialogA11y';

const ALL_SECTIONS: ProjectSectionKey[] = ['tabs', 'research', 'bookmarks', 'notes', 'highlighter', 'tasks'];

type Collaborator = {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'editor' | 'viewer';
};

interface ShareProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Session / workspace id (same as project `pid` / `sid` in backend). */
  projectSid: string;
  projectName?: string;
}

function memberToCollaborator(
  ownerEmail: string,
  members: ShareMember[],
  ownerFallbackName: string
): Collaborator[] {
  const list: Collaborator[] = [
    {
      id: 'owner',
      name: ownerEmail.split('@')[0] ?? ownerFallbackName,
      email: ownerEmail,
      role: 'owner',
    },
  ];
  members.forEach((m, i) => {
    list.push({
      id: `member-${i}`,
      name: m.email.split('@')[0] ?? m.email,
      email: m.email,
      role: m.role,
    });
  });
  return list;
}

const CollaboratorRow = ({
  collaborator,
  tCommon,
}: {
  collaborator: Collaborator;
  tCommon: (key: string) => string;
}) => {
  const initials = collaborator.name.split(' ').map((n) => n[0]).join('') || '?';
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary">
          {initials}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-bold text-foreground">{collaborator.name}</span>
          <span className="text-xs text-muted-foreground">{collaborator.email}</span>
        </div>
      </div>
      <span className="text-xs font-bold text-muted-foreground capitalize">
        {collaborator.role === 'owner'
          ? tCommon('shared.modals.shareProject.owner')
          : collaborator.role === 'editor'
            ? tCommon('shared.modals.shareProject.editor')
            : tCommon('shared.modals.shareProject.viewer')}
      </span>
    </div>
  );
};

export const ShareProjectModal: React.FC<ShareProjectModalProps> = ({
  isOpen,
  onClose,
  projectSid,
  projectName,
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tToast } = useTranslation('notifications');
  const [share, setShare] = useState<Share | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkAccess, setLinkAccess] = useState(true);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<ShareRole>('viewer');
  const [permissionDropdownOpen, setPermissionDropdownOpen] = useState(false);
  const [selectedSections, setSelectedSections] = useState<Set<ProjectSectionKey>>(
    () => new Set(ALL_SECTIONS)
  );
  const projectNameText = projectName || tCommon('shared.modals.shareProject.defaultName');

  const sectionLabel: Record<ProjectSectionKey, string> = {
    tabs: tCommon('shared.publicPage.tabs'),
    research: tCommon('session.category.research'),
    bookmarks: tCommon('shared.publicPage.bookmarks'),
    notes: tCommon('shared.modals.shareProject.notes'),
    highlighter: tCommon('shared.modals.shareProject.highlighter'),
    tasks: tCommon('shared.modals.shareProject.tasks'),
  };

  const shareUrl = share
    ? buildPublicShareUrl(share.publicLinkId)
    : `${resolveWebAppOrigin()}/t/...`;

  const toggleSection = (key: ProjectSectionKey) => {
    if (share) return;
    setSelectedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size <= 1) {
          notifications.error(tToast('shareProject.selectSectionShare'));
          return prev;
        }
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const sectionsList = (): ProjectSectionKey[] => ALL_SECTIONS.filter((s) => selectedSections.has(s));

  const loadShare = useCallback(async () => {
    if (!projectSid || !isOpen) return;
    setLoading(true);
    try {
      const { share: existing } = await getSharesByOwner(
        undefined,
        undefined,
        undefined,
        undefined,
        projectSid
      );
      if (existing && existing.shareType === 'project') {
        setShare(existing);
        setLinkAccess(existing.publicLinkEnabled);
        if (existing.sections?.length) {
          setSelectedSections(new Set(existing.sections));
        } else {
          setSelectedSections(new Set(ALL_SECTIONS));
        }
      } else {
        setShare(null);
        setLinkAccess(false);
        setSelectedSections(new Set(ALL_SECTIONS));
      }
    } catch (err) {
      console.error('Failed to load share', err);
      setShare(null);
      setLinkAccess(false);
      notifications.error(tToast('shareProject.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [projectSid, isOpen]);

  useEffect(() => {
    if (isOpen && projectSid) {
      loadShare();
    }
    if (!isOpen) {
      setShare(null);
      setInviteEmail('');
    }
  }, [isOpen, projectSid, loadShare]);

  const dialogRootRef = useRef<HTMLDivElement | null>(null);
  useDialogA11y(dialogRootRef, { isOpen, onClose });

  const handleCopy = () => {
    if (!share?.publicLinkId) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    notifications.success(tToast('shareProject.linkCopied'));
  };

  const handleToggleLinkAccess = async () => {
    const next = !linkAccess;
    if (!projectSid) return;
    const sections = sectionsList();
    if (sections.length === 0) {
      notifications.error(tToast('shareProject.selectSection'));
      return;
    }
    setSaving(true);
    try {
      if (share) {
        await updateShare(share.id, { publicLinkEnabled: next });
        setShare((s) => (s ? { ...s, publicLinkEnabled: next } : null));
        setLinkAccess(next);
        notifications.success(
          next ? tToast('shareProject.linkEnabled') : tToast('shareProject.linkDisabled')
        );
      } else if (next) {
        const { share: created } = await createShare({
          shareType: 'project',
          projectSid,
          sections,
          name: projectNameText,
          publicLinkEnabled: true,
          members: [],
        });
        setShare(created);
        setLinkAccess(true);
        notifications.success(tToast('shareProject.linkEnabledReadOnly'));
      } else {
        setLinkAccess(false);
      }
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('shareProject.updateLinkFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvites = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      notifications.error(tToast('shareProject.enterEmail'));
      return;
    }
    if (!projectSid) return;
    const sections = sectionsList();
    if (sections.length === 0) {
      notifications.error(tToast('shareProject.selectSection'));
      return;
    }
    setSaving(true);
    try {
      if (share) {
        const members = [...(share.members ?? []), { email, role: inviteRole }];
        await updateShare(share.id, { members });
        setShare((s) => (s ? { ...s, members } : null));
        setInviteEmail('');
        notifications.success(tToast('shareProject.invitationSent', { email }));
      } else {
        const { share: created } = await createShare({
          shareType: 'project',
          projectSid,
          sections,
          name: projectNameText,
          publicLinkEnabled: linkAccess,
          members: [{ email, role: inviteRole }],
        });
        setShare(created);
        setInviteEmail('');
        notifications.success(tToast('shareProject.shareCreatedInvitation', { email }));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send invite';
      notifications.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const collaborators = share
    ? memberToCollaborator(
        share.ownerEmail,
        share.members ?? [],
        tCommon('shared.modals.shareProject.owner')
      )
    : [{ id: 'owner', name: tCommon('shared.modals.shareProject.you'), email: '', role: 'owner' as const }];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div
        ref={dialogRootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-project-modal-title"
        tabIndex={-1}
        className="relative w-full max-w-xl min-w-0 bg-card modal-surface border border-border shadow-modal overflow-hidden flex flex-col animate-in zoom-in-95 fade-in duration-200 max-h-[90vh] focus:outline-none"
      >
        <div className="flex items-center justify-between px-10 pt-8 pb-4 shrink-0">
          <h2 id="share-project-modal-title" className="text-xl font-bold text-foreground flex items-center gap-2">
            <LayoutGrid size={22} className="text-primary" aria-hidden />
            {tCommon('shared.modals.shareProject.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={tCommon('common.close')}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-all"
          >
            <X size={20} aria-hidden />
          </button>
        </div>

        <div className="px-10 mb-4 shrink-0">
          <p className="text-sm text-muted-foreground leading-relaxed max-w-[90%]">
            {tCommon('shared.modals.shareProject.description')}
          </p>
        </div>

        <div className="px-10 pb-10 space-y-6 overflow-y-auto overflow-x-hidden custom-scrollbar text-left flex-1 min-h-0 min-w-0 overscroll-y-contain">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">{tCommon('common.loading')}</div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">
                  {tCommon('shared.modals.shareProject.sectionsInShare')}
                </label>
                {/* Column count follows modal width (not viewport), so iframe/extension sidebars don't get 2 ultra-narrow tracks */}
                <div
                  className="grid w-full gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,10.5rem),1fr))]"
                >
                  {ALL_SECTIONS.map((key) => (
                    <label
                      key={key}
                      className={clsx(
                        'flex w-full min-w-0 items-center gap-3 p-3 rounded-xl border border-border cursor-pointer transition-colors',
                        selectedSections.has(key) ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 opacity-70',
                        share && 'cursor-not-allowed opacity-90'
                      )}
                    >
                      <input
                        type="checkbox"
                        className="shrink-0 rounded border-border text-primary focus:ring-primary/30"
                        checked={selectedSections.has(key)}
                        disabled={Boolean(share)}
                        onChange={() => toggleSection(key)}
                      />
                      <span className="min-w-0 text-sm font-medium text-foreground">{sectionLabel[key]}</span>
                    </label>
                  ))}
                </div>
                {share && (
                  <p className="text-[11px] text-muted-foreground px-1">
                    {tCommon('shared.modals.shareProject.sectionListFixed')}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">
                  {tCommon('shared.modals.shareProject.defaultInvitePermission')}
                </label>
                <p className="text-xs text-muted-foreground px-1 -mt-1">
                  {tCommon('shared.modals.shareProject.defaultInvitePermissionHelp')}
                </p>
                <div className="flex gap-2 px-1">
                  <button
                    type="button"
                    onClick={() => setInviteRole('viewer')}
                    className={clsx(
                      'flex-1 py-2 rounded-xl text-xs font-bold border transition-colors',
                      inviteRole === 'viewer'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
                    )}
                  >
                    {tCommon('shared.modals.shareProject.view')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setInviteRole('editor')}
                    className={clsx(
                      'flex-1 py-2 rounded-xl text-xs font-bold border transition-colors',
                      inviteRole === 'editor'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
                    )}
                  >
                    {tCommon('shared.modals.shareProject.edit')}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">
                  {tCommon('shared.modals.shareProject.invitePeople')}
                </label>
                <div className="relative min-w-0 group">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                    size={18}
                  />
                  <input
                    type="email"
                    placeholder={tCommon('shared.modals.shareProject.emailPlaceholder')}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full min-w-0 bg-muted border border-border rounded-2xl py-3.5 pl-12 pr-[8.5rem] text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all font-medium"
                  />
                  <div className="absolute right-3 top-1/2 max-w-[calc(100%-3rem)] -translate-y-1/2 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPermissionDropdownOpen((o) => !o)}
                      className="flex items-center gap-2 px-3 py-2 bg-secondary hover:bg-muted text-[11px] font-bold text-muted-foreground border border-border rounded-lg transition-all active:scale-95"
                    >
                      {inviteRole === 'viewer' ? tCommon('shared.modals.shareProject.canView') : tCommon('shared.modals.shareProject.canEdit')} <ChevronDown size={14} />
                    </button>
                    {permissionDropdownOpen && (
                      <div className="dropdown-menu-panel absolute right-0 top-full z-10 mt-1 min-w-[120px] border border-border bg-card py-1 shadow-popover">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            setInviteRole('viewer');
                            setPermissionDropdownOpen(false);
                          }}
                        >
                          {tCommon('shared.modals.shareProject.canView')}
                        </button>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            setInviteRole('editor');
                            setPermissionDropdownOpen(false);
                          }}
                        >
                          {tCommon('shared.modals.shareProject.canEdit')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                {collaborators.map((collab) => (
                  <CollaboratorRow key={collab.id} collaborator={collab} tCommon={tCommon} />
                ))}
              </div>

              <div className="my-1 h-px shrink-0 bg-border" role="separator" />

              <div className="space-y-3">
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">
                  {tCommon('shared.modals.shareProject.publicLinkReadonly')}
                </label>
                <div className="p-4 bg-primary/[0.06] border border-primary/10 rounded-2xl flex items-center justify-between group">
                  <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-primary/10 rounded-xl text-primary">
                      <Globe size={20} />
                    </div>
                    <div className="ml-1">
                      <h4 className="text-sm font-bold text-foreground">{tCommon('shared.modals.shareProject.linkAccess')}</h4>
                      <p className="text-[11px] text-muted-foreground font-medium">
                        {tCommon('shared.modals.shareProject.anyoneCanPreview')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleLinkAccess}
                    disabled={saving}
                    className={clsx(
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20',
                      linkAccess ? 'bg-primary' : 'bg-muted'
                    )}
                  >
                    <span
                      className={clsx(
                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm',
                        linkAccess ? 'translate-x-6' : 'translate-x-1'
                      )}
                    />
                  </button>
                </div>
                {linkAccess && share && (
                  <div className="flex items-center gap-3 p-1 pl-4 bg-muted/50 border border-border rounded-2xl group focus-within:border-primary/40 transition-all">
                    <span className="flex-1 text-sm text-muted-foreground truncate font-medium">{shareUrl}</span>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className={clsx(
                        'flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold transition-all',
                        copied
                          ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                          : 'bg-muted text-primary hover:bg-secondary hover:text-primary'
                      )}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? tCommon('shared.modals.shareProject.copied') : tCommon('shared.modals.shareProject.copy')}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-6 pt-2">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">
                  {tCommon('shared.modals.shareProject.quickShare')}
                </span>
                <div className="flex items-center gap-2">
                  <a
                    href={
                      share
                        ? `mailto:?subject=${encodeURIComponent(tCommon('shared.modals.shareProject.mailSubject', { name: projectNameText }))}&body=${encodeURIComponent(shareUrl)}`
                        : '#'
                    }
                    className="p-2.5 bg-muted hover:bg-secondary text-muted-foreground hover:text-foreground rounded-xl transition-all hover:scale-110 active:scale-95"
                  >
                    <Mail size={18} />
                  </a>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="p-2.5 bg-muted hover:bg-secondary text-muted-foreground hover:text-foreground rounded-xl transition-all hover:scale-110 active:scale-95"
                  >
                    <Share2 size={18} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="relative z-10 mt-auto flex shrink-0 items-center justify-end gap-4 border-t border-border bg-card px-10 py-6">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
          >
            {tCommon('common.close')}
          </button>
          <button
            type="button"
            onClick={handleSendInvites}
            disabled={saving || !inviteEmail.trim()}
            className="px-8 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-2xl text-sm active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {tCommon('shared.modals.shareProject.sendInvites')}
          </button>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: hsl(var(--border));
          border-radius: 10px;
        }
      `,
        }}
      />
    </div>
  );
};
